import type { StoreApi } from 'zustand';
import type { AppState } from './store';
import { garmentRgb } from './store';
import { nextGeneration } from './runner';
import { printWidthFor, separationFor } from './sep';
import { isStale, type EngineApi } from '../worker/protocol';

export interface SepRunnerDeps {
  api: Pick<EngineApi, 'buildSeparation' | 'detectInks' | 'updateSeparation'>;
  debounceMs?: number;
}

/** Trailing-edge debounce for `updateFilms`; see `pushFilms` for why. */
export const FILMS_DEBOUNCE_MS = 150;

/**
 * Debounced screen-print separation builder, mirroring `createRunner`'s
 * generation/staleness discipline: one build per doc in flight, a newer
 * request always wins, and a build that lands after a newer one started is
 * dropped rather than clobbering it.
 */
export function createSepRunner(store: StoreApi<AppState>, deps: SepRunnerDeps) {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Separate from `timers`: a films push and a plate rebuild for the same doc
   *  are independent, and sharing one timer per doc would let either cancel the
   *  other. */
  const filmTimers = new Map<string, ReturnType<typeof setTimeout>>();
  /** Docs whose build was deferred because a DTF run was still in flight when
   *  it was requested (see `build`); retried once `running` clears, whether
   *  that run succeeded or REJECTED — a rejection leaves `doc.run` untouched,
   *  so nothing else would ever notice the pending edit. */
  const pendingBuild = new Set<string>();
  /** The most recent `detect` call's token per doc, so an earlier concurrent
   *  call that resolves after a later one for the SAME doc is dropped rather
   *  than clobbering the newer result. */
  const detectTokens = new Map<string, number>();
  let disposed = false;

  function schedule(id: string, work: () => void) {
    const pending = timers.get(id);
    if (pending) clearTimeout(pending);
    timers.set(id, setTimeout(() => { timers.delete(id); work(); }, deps.debounceMs ?? 250));
  }

  /**
   * Claim the doc's next build generation NOW, before the debounce, and mark it
   * building. Everything that decides whether a result may be written reads
   * `buildGeneration`, so a generation allocated only when `build()` finally
   * runs leaves the whole debounce window (250 ms, and all of `detectInks`)
   * looking like nothing newer had been asked for: an older build resolving in
   * there passed `isStale` and wrote its plates, so the panel flashed
   * "Built N plates" and re-enabled "Export films" against art the user had
   * already superseded — long enough for an export to start on it.
   *
   * Reserving up front makes that result stale from the instant the newer build
   * is REQUESTED. The cost is a burned generation per superseded trigger, which
   * is free: `nextGeneration` is a counter and the comparisons are all `<`.
   */
  function reserve(id: string): number {
    const gen = nextGeneration();
    store.getState().setDocSep(id, { building: true, buildGeneration: gen, error: undefined });
    return gen;
  }

  async function build(id: string, gen: number) {
    const st = store.getState();
    const doc = st.docs.find((d) => d.id === id);
    if (!doc || !doc.sep) return;
    if (doc.running) { pendingBuild.add(id); return; }   // retried by `unsubRunning` below once `running` clears
    pendingBuild.delete(id);
    // Something reserved a newer generation while this one waited out its
    // debounce (a `detect` mid-window, say): that build owns the doc now.
    if (isStale(gen, doc.sep.buildGeneration ?? gen)) return;
    // No DTF output to separate and no run in flight to wait for (a REJECTED run):
    // `reserve` already marked the doc building, so clear it here or the panel
    // reads "Building…" for good. Checked after the staleness gate so a newer
    // build's flag is never cleared by an older one.
    if (!doc.run) { store.getState().setDocSep(id, { building: false }); return; }
    const separation = doc.sep.separation;
    const printWidthIn = printWidthFor(st.settings, doc.run);
    // Captured BEFORE the await: a DTF run landing WHILE this build is in
    // flight must not relabel these plates with the newer generation — they
    // were built from the output that was current when the build STARTED.
    const runGen = doc.run.generation;
    try {
      const result = await deps.api.buildSeparation(id, {
        separation,
        garment: garmentRgb(st.settings.garmentHex),
        artDpi: doc.run.dpi,
        printWidthIn,
      }, gen);
      if (disposed) return;
      const current = store.getState().docs.find((d) => d.id === id);
      if (!current || !current.sep) return;
      if (isStale(gen, current.sep.buildGeneration ?? gen)) return;   // a newer build already superseded this one
      store.getState().setDocSep(id, {
        building: false,
        plates: result.plates,
        checks: result.checks,
        unmatched: result.unmatched,
        indexSize: result.indexSize,
        builtGeneration: gen,
        runGeneration: runGen,
        ms: result.ms,
        error: undefined,
      });
    } catch (err) {
      if (disposed) return;
      const current = store.getState().docs.find((d) => d.id === id);
      if (!current || !current.sep) return;
      if (isStale(gen, current.sep.buildGeneration ?? gen)) return;
      store.getState().setDocSep(id, { building: false, error: (err as Error).message });
    }
  }

  function triggerBuild(id: string) {
    const gen = reserve(id);
    schedule(id, () => void build(id, gen));
  }

  /**
   * Push the doc's current screen/film settings to the worker without a rebuild
   * (a 'films' separation change — see `sepChangeKind`), then bump `filmsVersion`
   * so the film/proof/plate previews that key on it refetch.
   *
   * Debounced on a trailing edge, on its own per-doc timer. The push itself is
   * cheap (a settings swap and a cache clear), but the `filmsVersion` bump it
   * lands is not: it invalidates every cached film, and re-rendering one is
   * around 1.7 s at 15 in / 720 dpi. Dragging an LPI or a film-margin slider
   * fires one store change per tick, so without this the worker would be re-
   * rendering films for settings the user has already scrolled past. 150 ms is
   * short enough to feel immediate on release and long enough to swallow a drag.
   *
   * The worker hands back the separation checks recomputed against the new
   * screening settings (moiré, mesh-vs-LPI, art resolution — spec §7 lists them as
   * live), so they land with the `filmsVersion` bump instead of waiting for a
   * rebuild that a screening edit never schedules. `null` means the doc has no
   * build yet: keep whatever checks it has.
   *
   * Sets and clears `filmsError` ONLY — never `error`. `error` is the last BUILD's
   * failure, and `separationReadiness` keys export-blocking on it: a films push
   * clearing it would re-enable exporting the failed build's older plates.
   */
  async function pushFilms(id: string) {
    const doc = store.getState().docs.find((d) => d.id === id);
    if (!doc?.sep) return;
    const separation = doc.sep.separation;
    try {
      const result = await deps.api.updateSeparation(id, separation);
      if (disposed) return;
      const current = store.getState().docs.find((d) => d.id === id);
      if (!current?.sep) return;
      store.getState().setDocSep(id, {
        filmsVersion: current.sep.filmsVersion + 1,
        ...(result ? { checks: result.checks } : {}),
        filmsError: undefined,
      });
    } catch (err) {
      if (disposed) return;
      const current = store.getState().docs.find((d) => d.id === id);
      if (!current?.sep) return;
      store.getState().setDocSep(id, { filmsError: (err as Error).message });
    }
  }

  // A build deferred by `pendingBuild` (the DTF run was still in flight) is
  // retried the moment that run's `running` flag clears — regardless of
  // whether `run` itself changed, since a rejected run leaves it untouched.
  const unsubRunning = store.subscribe((st, prev) => {
    if (pendingBuild.size === 0) return;
    for (const doc of st.docs) {
      if (!pendingBuild.has(doc.id)) continue;
      const prevDoc = prev.docs.find((d) => d.id === doc.id);
      // Deleted before re-triggering: `reserve` writes to the store, which
      // re-enters this subscription, and a doc still listed as pending would
      // be looked at twice for one settling run.
      if (prevDoc?.running && !doc.running) { pendingBuild.delete(doc.id); triggerBuild(doc.id); }
    }
  });

  return {
    trigger(id: string) { triggerBuild(id); },

    updateFilms(id: string) {
      const pending = filmTimers.get(id);
      if (pending) clearTimeout(pending);
      filmTimers.set(id, setTimeout(() => { filmTimers.delete(id); void pushFilms(id); }, FILMS_DEBOUNCE_MS));
    },

    /**
     * Detect inks, store them on the document's separation (creating it from
     * the shop defaults first if the document has never had one), then
     * trigger the usual debounced build. Not itself debounced — the caller
     * (a button click) already gates it. Tokened per doc so a second,
     * concurrent detect for the same doc always wins over the first,
     * whichever order their results actually land in.
     */
    async detect(id: string, count: number): Promise<void> {
      const st = store.getState();
      const doc = st.docs.find((d) => d.id === id);
      if (!doc) return;
      const token = (detectTokens.get(id) ?? 0) + 1;
      detectTokens.set(id, token);
      if (!doc.sep) {
        store.getState().setSeparation(id, separationFor(undefined, st.sepDefaults, garmentRgb(st.settings.garmentHex)));
      }
      // Reserved here, not just when the build that follows is scheduled: the
      // whole of `detectInks` is otherwise a window in which an older build's
      // result still looks current.
      reserve(id);
      const mode = store.getState().docs.find((d) => d.id === id)!.sep!.separation.mode;
      try {
        const inks = await deps.api.detectInks(id, count, mode);
        if (disposed || detectTokens.get(id) !== token) return;   // superseded by a later concurrent detect
        const current = store.getState().docs.find((d) => d.id === id);
        if (!current || !current.sep) return;
        store.getState().setSeparation(id, { ...current.sep.separation, inks });
        triggerBuild(id);
      } catch (err) {
        if (disposed || detectTokens.get(id) !== token) return;
        const current = store.getState().docs.find((d) => d.id === id);
        if (!current || !current.sep) return;
        store.getState().setDocSep(id, { building: false, error: (err as Error).message });
      }
    },

    dispose() {
      disposed = true;
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
      for (const t of filmTimers.values()) clearTimeout(t);
      filmTimers.clear();
      pendingBuild.clear();
      detectTokens.clear();
      unsubRunning();
    },
  };
}
